import { greenStyles, errorStyles, grayStyles, blueStyles } from "components/Tag/Status/styles";
import { sortDateTime } from "helpers/sort";
import { isInPast } from "helpers/time";
import { titleize } from "helpers/string";
import { Message } from "features/Resources/Message/models";
import { Email } from "features/Resources/Email/models";
import { Task } from "features/Resources/Task/models";
import { taskPrimaryTriggerDate } from "features/Resources/Task/helpers";
import { RelationType } from "models/v2/onboardingJourneyNetworkRelation";
import { templatePathByTypeAndId } from "helpers/url/pages/v2/actionPaths";
import { TemplatePrefixId } from "features/Resources/SharedTemplate/models";
import { EmailRecipient } from "features/Resources/Email/models/EmailRecipient";
import { isUserEmailRecipient, isRoleEmailRecipient, isCustomEmailRecipient } from "features/Resources/Email/helpers";
import { meetingScheduledDate } from "features/Resources/Meeting/helpers";
import { Meeting } from "features/Resources/Meeting/models";
import {
  ACTION_TYPES,
  ActionSummaryData,
  ActionType,
  ActionV2,
  EmailActionSummary,
  MessageActionSummary,
  PrefixId,
  TaskActionSummary,
  TemplateType,
  ActionSummary,
} from "../models";
import moment, { Moment } from "moment";
import { lightActionStyles } from "../styles";
import { DEFAULT_NO_END_MINUTES, RESOLVED_ACTION_STATUSES } from "../constants";
import { CalendarEvent } from "components/Calendar";

interface ToTimeFields {
  datetime: Date;
  fieldName: string;
  timezone: string;
}

export function toFormDateTimeFields({ datetime }: ToTimeFields) {
  return {
    fieldName: datetime,
    localFields: {
      date: datetime.toLocaleDateString(),
      time: datetime.toLocaleTimeString(),
    },
    year: datetime.getFullYear(),
    month: datetime.getMonth() + 1,
    day: datetime.getDate(),
    hour: datetime.getHours(),
    minute: datetime.getMinutes(),
    second: datetime.getSeconds(),
  };
}

type ReturnFromPrefixId = {
  resourceType?: ActionType;
  id?: string;
};

export function fromActionPrefixId(prefixId: PrefixId | undefined | ""): ReturnFromPrefixId {
  if (!prefixId) {
    return {};
  }
  const match = prefixId.match(/(.+?)_(\d+)$/);
  if (match) {
    const [, resourceType, id] = match;
    return { resourceType: resourceType as unknown as ActionType, id };
  }
  return {};
}

export function isActionPrefixId(prefixId: PrefixId) {
  if (!prefixId) return false;
  const { resourceType } = fromActionPrefixId(prefixId);
  return ACTION_TYPES.includes(resourceType as any);
}

export function toActionPrefixId(resource: ActionType | TemplateType, id: string) {
  return `${resource}_${id}`;
}

export function displayActionType(action: ActionV2 | ActionSummaryData) {
  const { resourceType } = fromActionPrefixId(action?.prefixId);
  return resourceType ? titleize(resourceType) : "";
}

export function isActionSummary(obj: any | undefined): obj is ActionSummary {
  return obj && typeof obj.actionType === "string" && "action" in obj;
}

export function extractAction(
  actionOrSummary: ActionV2 | ActionSummary | undefined,
): ActionV2 | ActionSummaryData | undefined {
  return isActionSummary(actionOrSummary) ? actionOrSummary?.action : actionOrSummary;
}

function someIncludeName(emailRecipients: EmailRecipient[], text: string) {
  return (emailRecipients || []).some((emailRecipient) => {
    // Check user's full name for user recipients
    if (
      isUserEmailRecipient(emailRecipient) &&
      typeof emailRecipient.user?.fullName === "string" &&
      emailRecipient.user.fullName.toLowerCase().includes(text)
    ) {
      return true;
    }
    // Check onboarding journey network relation name for role recipients
    if (
      isRoleEmailRecipient(emailRecipient) &&
      typeof emailRecipient.onboardingJourneyNetworkRelation?.name === "string" &&
      emailRecipient.onboardingJourneyNetworkRelation.name.toLowerCase().includes(text)
    ) {
      return true;
    }
    return false;
  });
}

function someIncludeCustomEmail(emailRecipients: EmailRecipient[], text: string) {
  return (emailRecipients || []).some((emailRecipient) =>
    isCustomEmailRecipient(emailRecipient) && typeof emailRecipient.customEmail === "string"
      ? emailRecipient.customEmail.toLowerCase().includes(text)
      : false,
  );
}

export function filterActionSummaryByText(actions: ActionSummary[] | undefined, searchText: string | "") {
  if (!actions) return [];
  if ((searchText || "").length < 3) {
    return actions;
  }

  const text = searchText.toLowerCase();
  return actions.filter(
    (action) =>
      action?.action?.name?.toLowerCase()?.includes(text) ||
      action?.action?.status?.toLowerCase()?.includes(text) ||
      (action as TaskActionSummary)?.action?.assignee?.fullName?.toLowerCase().includes(text) ||
      (action as MessageActionSummary)?.action?.recipient?.fullName?.toLowerCase().includes(text) ||
      (action as MessageActionSummary)?.action?.recipient?.name?.toLowerCase().includes(text) ||
      someIncludeCustomEmail((action as EmailActionSummary)?.action?.to, text) ||
      someIncludeName((action as EmailActionSummary)?.action?.to, text),
  );
}

export function filterByActionType(actions: ActionSummary[] | undefined, actionType: ActionType | "") {
  if (!actions) return [];
  if (!actionType) return actions;

  return actions.filter((action) => action?.actionType === actionType);
}

export function filterByTargetRelationType(
  actions: ActionSummary[] | undefined,
  relationTargetType: RelationType | "",
) {
  if (!actions) return [];
  if (!relationTargetType) return actions;

  return actions.filter((action) => action?.targetType === relationTargetType);
}

export function messageEmailPrimaryTriggerDate(action: Message | Email) {
  return action?.deliveredAt ?? action?.deliverAt;
}

export function primaryTriggerDate(action: ActionSummary | ActionV2) {
  const resolvedAction = extractAction(action);
  const { resourceType } = fromActionPrefixId(resolvedAction?.prefixId);
  switch (resourceType) {
    case "message":
    case "email":
      return messageEmailPrimaryTriggerDate(resolvedAction as Email | Message);
    case "task":
      return taskPrimaryTriggerDate(resolvedAction as Task);
    case "meeting":
      return meetingScheduledDate(resolvedAction as Meeting);
    default:
      return undefined;
  }
}

export function messageEmailStatusInfo(action: Message | Email) {
  if (action.status === "delivered") {
    return { colorScheme: greenStyles, text: "Delivered", statusLabel: "Sent At", isDone: true };
  } else if (action.status === "failed") {
    return { colorScheme: errorStyles, text: "Failed", statusLabel: "Failed At", isDone: false };
  } else if (action.status === "skipped") {
    return { colorScheme: grayStyles, text: "Skipped", statusLabel: "Skipped At", isDone: false };
  }
  return { colorScheme: {}, text: null, statusLabel: "Scheduled At", isDone: false };
}

export function taskStatusInfo(action: Task) {
  if (action.status === "completed") {
    return { colorScheme: greenStyles, text: "Completed", statusLabel: "Start At", isDone: true };
  } else if (action.status === "failed") {
    return { colorScheme: errorStyles, text: "Failed", statusLabel: "Failed At", isDone: false };
  } else if (action.status === "skipped") {
    return { colorScheme: grayStyles, text: "Skipped", statusLabel: "Skipped At", isDone: false };
  } else if (action.assigneeNotifiedAt) {
    return { colorScheme: blueStyles, text: "Notification Sent", statusLabel: "Notification Sent", isDone: false };
  }
  return { colorScheme: {}, text: null, statusLabel: "", isDone: false };
}

export function actionSummaryStatusInfo(actionSummary: ActionSummary) {
  switch (actionSummary.actionType) {
    case "message":
    case "email":
      return messageEmailStatusInfo(actionSummary?.action as Email | Message);
    case "task":
      return taskStatusInfo(actionSummary?.action as Task);
    default:
      return { text: null, isDone: false };
  }
}

export function actionStatusInfo(action: ActionSummaryData | ActionV2 | undefined) {
  const { resourceType } = fromActionPrefixId(action?.prefixId);
  switch (resourceType) {
    case "message":
    case "email":
      return messageEmailStatusInfo(action as Email | Message);
    case "task":
      return taskStatusInfo(action as Task);
    default:
      return { text: null, isDone: false };
  }
}

/**
 * An action is "resolved" when it is done (completed/delivered) or terminally closed
 * without completing (skipped, removed, cancelled, or — for tasks — closed_stale).
 * Centralizes the scattered `isDone || status === "skipped"` checks; the status set
 * lives in RESOLVED_ACTION_STATUSES.
 */
export function isActionResolved(action: ActionV2 | ActionSummaryData | undefined): boolean {
  return !!action && RESOLVED_ACTION_STATUSES.includes(action.status);
}

export function isProcessed(action: ActionSummary["action"]) {
  // `failed` is recoverable (skip / retry / delete) — not terminal — so we treat it
  // as still-actionable here. Otherwise downstream menus hide every action and the
  // user has no way to deal with a failed send from the UI.
  return !["upcoming", "pending", "scheduled", "failed"].includes(action?.status);
}

export function sortActionsByTriggerDate(
  actions: Array<ActionSummary>,
  direction: "asc" | "desc" = "asc",
): Array<ActionSummary> {
  return sortDateTime(actions, (action) => primaryTriggerDate(action), direction);
}

export function isImmediate(action: ActionSummary | ActionV2 | undefined): boolean {
  const resolvedAction = extractAction(action);
  return resolvedAction?.deliverImmediately || resolvedAction?.startImmediately;
}

export function isDependencyTriggered(action: ActionSummary | ActionV2 | undefined): boolean {
  const resolvedAction = extractAction(action);
  return resolvedAction?.deliverAfterDependencyId || resolvedAction?.startAfterDependencyId;
}

export function removeLocalData<T extends Record<string, any>>(updateData: T): Omit<T, "local"> {
  // remove local data if exists, which is sometimes there for fields that we use for composite fields in forms
  const { local, ...rest } = updateData;
  return rest;
}

export function mainTriggerDate(action: ActionV2) {
  const { resourceType } = fromActionPrefixId(action?.prefixId);
  switch (resourceType) {
    case "message":
    case "email":
      return (action as Email | Message).deliverAt;
    case "task":
      return (action as Task).startAt;
    default:
      return undefined;
  }
}

export function isActionTimingValid(action: ActionV2) {
  if (!action) return false;

  const triggerDate = mainTriggerDate(action);
  if (isActionResolved(action)) {
    return true;
  }

  return triggerDate && !isInPast(triggerDate);
}

function actionTypeToTemplateType(actionType: ActionType): TemplateType {
  return `${actionType}_template`;
}

export function actionTemplatePathById(action: ActionV2) {
  if (!action) return "";

  const { resourceType } = fromActionPrefixId(action.prefixId);

  const templateType = actionTypeToTemplateType(resourceType as ActionType);

  return templatePathByTypeAndId(templateType, action?.templateId);
}

export function actionTemplatePrefixId(action: ActionV2): TemplatePrefixId | "" {
  if (!action) return "";

  const { resourceType } = fromActionPrefixId(action.prefixId);

  const templateType = actionTypeToTemplateType(resourceType as ActionType);

  return `${templateType}_${action.templateId}`;
}

export function groupActionsByDependencyId(actions: ActionSummary[]) {
  return actions.reduce<Record<string, ActionSummary[]>>((acc, a) => {
    if (!a.unifiedDependencyId) return acc;
    if (!acc[a.unifiedDependencyId]) acc[a.unifiedDependencyId] = [];
    acc[a.unifiedDependencyId].push(a);
    return acc;
  }, {});
}

export function groupActionsByDate(actions: ActionSummary[], timezone: string) {
  const sorted = [...actions].sort(
    (a, b) => moment.tz(a.unifiedDate, timezone).valueOf() - moment.tz(b.unifiedDate, timezone).valueOf(),
  );

  const actionMap = new Map<string, ActionSummary[]>();
  sorted.forEach((a) => {
    let dateKey;
    if (a.unifiedDate) {
      dateKey = moment.tz(a.unifiedDate, timezone).format("YYYY-MM-DD");
    } else if (a.unifiedDependencyId) {
      dateKey = "dependency";
    } else if (a.unifiedIsImmediate) {
      dateKey = "immediate";
    } else {
      dateKey = "unknown";
    }
    if (!actionMap.has(dateKey)) actionMap.set(dateKey, []);
    actionMap.get(dateKey)!.push(a);
  });
  return actionMap;
}

export function groupActionsByPrefixId(actions: ActionSummary[]) {
  const actionMap = new Map<string, ActionSummary>();
  actions.forEach((a) => {
    actionMap.set(a.prefixId, a);
  });
  return actionMap;
}

export function groupActionsByDateAndHour(actions: ActionSummary[], timezone: string) {
  const sorted = [...actions].sort(
    (a, b) => moment.tz(a.unifiedDate, timezone).valueOf() - moment.tz(b.unifiedDate, timezone).valueOf(),
  );

  const actionMap = new Map<string, ActionSummary[]>();
  sorted.forEach((a) => {
    let slotKey;
    if (a.unifiedDate) {
      const dateMoment = moment.tz(a.unifiedDate, timezone);
      const dateKey = dateMoment.format("YYYY-MM-DD");
      const hour = dateMoment.hour();
      slotKey = `${dateKey}-${hour}`;
    } else if (a.unifiedDependencyId) {
      slotKey = "dependency";
    } else if (a.unifiedIsImmediate) {
      slotKey = "immediate";
    } else {
      slotKey = "unknown";
    }

    if (!actionMap.has(slotKey)) actionMap.set(slotKey, []);
    actionMap.get(slotKey)!.push(a);
  });
  return actionMap;
}

export function findEarliestHour(startOfWeek: Moment, actions: ActionSummary[], timezone: string) {
  const weekStart = startOfWeek.clone();
  const weekEnd = startOfWeek.clone().add(6, "days");

  let earliestHour = 8; // Default to 8 AM

  actions.forEach((action) => {
    if (action.unifiedDate) {
      const eventMoment = moment.tz(action.unifiedDate, timezone);
      if (eventMoment.isBetween(weekStart, weekEnd, "day", "[]")) {
        const eventHour = eventMoment.hour();
        if (eventHour < earliestHour) {
          earliestHour = eventHour;
        }
      }
    }
  });
  return earliestHour;
}

export function messageEmailIsEditable(action: ActionSummaryData | ActionV2) {
  return !messageEmailStatusInfo(action as Message | Email).isDone;
}

function actionCanBeMoved(action: ActionSummary) {
  switch (action.actionType) {
    case "meeting":
      return action.action?.status !== "pending_scheduling";
    case "task":
      return true;
    case "email":
    case "message":
      return messageEmailIsEditable(action.action);
    default:
      return false;
  }
}

export function actionToCalendarEvent(action: ActionSummary, timezone: string): CalendarEvent {
  const momentStart = moment.tz(action.unifiedDate, timezone);
  const start = momentStart.toDate();
  const end = momentStart
    .clone()
    .add(action.action?.durationMinutes || DEFAULT_NO_END_MINUTES, "minutes")
    .toDate();
  return {
    id: action.prefixId,
    title: action.action?.name,
    start,
    end,
    canDrag: actionCanBeMoved(action),
    canResize: action.actionType === "meeting" && action.action.status !== "pending_scheduling", // Only meetings can be resized
    style: lightActionStyles(action.action),
    eventType: action?.actionType,
    showEnd: action.actionType === "meeting", // only show the end time for month view for meetings
    data: action,
  };
}

export function actionStageName(startDate: Date, action: ActionSummary) {
  if (isImmediate(action)) {
    return "immediate";
  }
  if (isDependencyTriggered(action)) {
    return "dependency";
  }

  return stageKeyCalculator(startDate, primaryTriggerDate(action));
}
